import { useMemo } from 'react';

import { useCfis } from '../cfis-api/useCfis';
import { useFetchMileageTiersQuery } from './mileageTiersService';

import { OptionalProduct } from '~/core/models';
import { createFetchProductsHook } from '~/core/utils/createFetchProductsHook';

interface useMileageTiersReturnType {
  /**
   * Mileage tier that matches the distance from user to CFI
   */
  matchingMileageTier?: OptionalProduct;
}
/**
 * * Parses {@link distanceRange} string with a form of min-max. And return and object with the min max as properties
 * @param distanceRange a string with the distance range written as min-max like 61-80 where 61 is the min and 80 the max
 * Also distance range may be written as 5+. Which means 5 is the min and max is Infinity
 * @example
 * Param of distanceRange = 61-80. Returns { min: 61, max: 80}
 * * @example
 * Param of distanceRange = 5+. Returns { min: 5, max: Infinity}
 */
export const parseDistanceRange = (
  distanceRange: string | null
): { min: number; max: number } => {
  // Fallback is the value used for [notUsed, min, max]. The first element of the array is not used for anything so is set to null.
  const fallback: [null, number, number] = [null, 0, Infinity];
  const [_, min, max] = /(\d+)[+-]+(\d*)/.exec(distanceRange ?? '') ?? fallback;

  return {
    min: +min,
    max: max ? +max : Infinity,
  };
};

const _useMileageTiers = createFetchProductsHook({
  apiQueryHook: useFetchMileageTiersQuery,
});

export const useMileageTiers = (): useMileageTiersReturnType => {
  const { cfi } = useCfis();

  const { data: mileageTiers } = _useMileageTiers();

  const matchingMileageTier: OptionalProduct | undefined = useMemo(
    () =>
      [...(mileageTiers ?? [])]
        // Sorting to get highest price in case there is more tha one match
        .sort((a: OptionalProduct, b: OptionalProduct) => b.price - a.price)
        .find((mileageTier: OptionalProduct) => {
          const distanceRange = parseDistanceRange(mileageTier.qteSubCode);
          const distance = cfi?.distance;

          return (
            !!distance &&
            distance >= distanceRange.min &&
            distance < distanceRange.max
          );
        }),
    [cfi?.distance, mileageTiers]
  );

  return {
    matchingMileageTier,
  };
};
